import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import apiClient from "../../api/client";
import type { AuthUser } from "@repo/types";
import { LoginBrandPanel } from "./components/LoginBrandPanel";
import { OtpLoginForms } from "./components/OtpLoginForms";
import { RequestAccessForm } from "./components/RequestAccessForm";

type Step = "password" | "email" | "otp" | "request-access";

interface ApiEnvelope<T> {
  data: T;
  timestamp: string;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth, isAuthenticated } = useAuth();

  const from =
    (location.state as { from?: { pathname?: string } } | null)?.from
      ?.pathname ?? "/dashboard";

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const [step, setStep] = useState<Step>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [devOtp, setDevOtp] = useState<string | null>(null);
  const [reqName, setReqName] = useState("");
  const [reqEmail, setReqEmail] = useState("");
  const [reqReason, setReqReason] = useState("");
  const [reqSuccess, setReqSuccess] = useState(false);

  useEffect(() => {
    setEmail("");
    setPassword("");
    setOtp("");
  }, []);

  async function handleSendOtp(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);
    setLoading(true);
    try {
      const res = await apiClient.post<{
        data: { message: string; devOtp?: string };
        timestamp: string;
      }>("/auth/otp/send", { email });
      setStep("otp");
      const devOtpValue = res.data.data?.devOtp;
      if (devOtpValue) {
        setDevOtp(devOtpValue);
        setOtp(devOtpValue);
        setSuccessMsg("OTP auto-filled (dev mode).");
      } else {
        setSuccessMsg(`OTP sent to ${email}. Check your inbox.`);
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string; error?: string } } })
          ?.response?.data?.message ?? "Failed to send OTP. Please try again.";
      setError(Array.isArray(message) ? message.join(", ") : message);
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyOtp(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const response = await apiClient.post<
        ApiEnvelope<{ accessToken: string }>
      >("/auth/otp/verify", { email, otp });
      const accessToken = response.data.data.accessToken;
      const meResponse = await apiClient.get<ApiEnvelope<AuthUser>>(
        "/auth/me",
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      setAuth(meResponse.data.data, accessToken);
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Invalid OTP. Please try again.";
      setError(Array.isArray(message) ? message.join(", ") : message);
      setOtp("");
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordLogin(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const response = await apiClient.post<
        ApiEnvelope<{ accessToken: string }>
      >("/auth/password/login", { email, password });
      const accessToken = response.data.data.accessToken;
      const meResponse = await apiClient.get<ApiEnvelope<AuthUser>>(
        "/auth/me",
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      setAuth(meResponse.data.data, accessToken);
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Invalid email or password.";
      setError(Array.isArray(message) ? message.join(", ") : message);
      setPassword("");
    } finally {
      setLoading(false);
    }
  }

  function handleBack() {
    setStep("password");
    setPassword("");
    setOtp("");
    setError(null);
    setSuccessMsg(null);
    setDevOtp(null);
  }

  async function handleRequestAccess(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await apiClient.post("/auth/access-request", {
        name: reqName,
        email: reqEmail,
        reason: reqReason || undefined,
      });
      setReqSuccess(true);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Failed to submit request.";
      setError(Array.isArray(message) ? message.join(", ") : message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-saas-bg flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-4xl">
        <div className="bg-white rounded-2xl shadow-card-hover overflow-hidden flex flex-col sm:flex-row min-h-[520px]">
          <LoginBrandPanel />

          {/* Right Form Panel */}
          <div className="flex-1 flex flex-col justify-center px-6 py-8 sm:px-10 sm:py-10">
            {step === "request-access" ? (
              <RequestAccessForm
                reqName={reqName}
                reqEmail={reqEmail}
                reqReason={reqReason}
                reqSuccess={reqSuccess}
                loading={loading}
                error={error}
                onNameChange={setReqName}
                onEmailChange={setReqEmail}
                onReasonChange={setReqReason}
                onSubmit={(e) => void handleRequestAccess(e)}
                onBackToLogin={() => {
                  setStep("password");
                  setEmail("");
                  setPassword("");
                  setError(null);
                }}
                onSuccessBack={() => {
                  setStep("password");
                  setEmail("");
                  setPassword("");
                  setReqSuccess(false);
                }}
              />
            ) : (
              <OtpLoginForms
                step={step as "password" | "email" | "otp"}
                email={email}
                password={password}
                otp={otp}
                loading={loading}
                error={error}
                successMsg={successMsg}
                devOtp={devOtp}
                onEmailChange={setEmail}
                onPasswordChange={setPassword}
                onOtpChange={setOtp}
                onPasswordLogin={handlePasswordLogin}
                onSendOtp={handleSendOtp}
                onVerifyOtp={handleVerifyOtp}
                onBack={handleBack}
                onUseOtp={() => {
                  setStep("email");
                  setPassword("");
                  setError(null);
                  setSuccessMsg(null);
                }}
                onUsePassword={() => {
                  setStep("password");
                  setPassword("");
                  setError(null);
                  setSuccessMsg(null);
                  setDevOtp(null);
                }}
                onRequestAccess={() => {
                  setStep("request-access");
                  setEmail("");
                  setPassword("");
                  setError(null);
                  setReqSuccess(false);
                }}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-4">
          &copy; {new Date().getFullYear()} KSP Traders. All rights reserved.
        </p>
      </div>
    </div>
  );
}
